<template>
  <BModal hide-header hide-footer no-close-on-backdrop size="xl" body-class="rounded-lg p-lg-5 bg-white" :visible="visible" @hide="close">
    <div class="position-relative d-flex">
      <img class="zip d-none d-lg-block" src="/img/temp-dash/zip/temp-dash-zip-1.png" />
      <img
        class="d-block mx-auto mb-3"
        style="width: 232px"
        :src="industry == 'cannabis' ? '/img/logos/apex-logo.png' : '/img/logos/bushel-logo.png'"
        :alt="industry == 'cannabis' ? 'Apex Logo' : 'Bushel44 Logo'"
      />
    </div>
    <div class="welcome-banner text-white text-center rounded p-3 mb-4">
      <h4 class="mb-2">Welcome to {{ appName }}!</h4>
      <p class="mb-0">Add your locations to get started. This will be anywhere you are purchasing for. Feel free to add as many as you need!</p>
    </div>

    <div v-if="suggestions.length" class="mb-4 border rounded p-3">
      <div v-if="suggestionError" class="alert alert-danger py-2 mb-3">Something went wrong. Please try again.</div>
      <p class="font-weight-bold mb-3">
        {{ sellerCompanyName }} has your email associated with these locations. Would you like to add any of them to your account?
      </p>
      <div
        v-for="suggestion in suggestions"
        :key="suggestion.name + suggestion.state_license"
        class="suggestion-row d-flex align-items-center justify-content-between px-3 py-2 mb-2 rounded"
      >
        <div>
          <div class="font-weight-bold">{{ suggestion.name }}</div>
          <div class="text-sm text-muted">
            {{ [suggestion.line1, suggestion.city, suggestion.state?.text].filter(Boolean).join(", ") }}
          </div>
          <div v-if="suggestion.state_license" class="text-sm text-muted">License: {{ suggestion.state_license }}</div>
        </div>
        <div class="d-flex align-items-center ml-3">
          <BushelBtn
            variant="success"
            class="btn-sm text-nowrap"
            :disabled="creatingSuggestion === suggestion"
            @click="createFromSuggestion(suggestion)"
          >
            <div v-if="creatingSuggestion === suggestion" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <span v-else>Add location</span>
          </BushelBtn>
          <button
            type="button"
            class="close ml-2"
            aria-label="Dismiss"
            @click="
              suggestions = suggestions.filter((s) => s !== suggestion);
              suggestionError = false;
            "
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </div>
    </div>

    <div class="border shadow rounded p-3 p-md-5">
      <div v-if="createdOperations.length" class="mb-4">
        <h6 class="mb-2">Saved Locations</h6>
        <div v-for="op in createdOperations" :key="op.id">
          <div v-if="editingOperation?.id === op.id" class="created-operation-row p-3 mb-1 rounded">
            <h6 class="mb-3 text-primary">Edit Location</h6>
            <OperationForm
              :initial-values="op"
              :countries="countries"
              :countries-loading="countriesLoading"
              :operation-types="operationTypes"
              :saving="updating"
              :error="updateError"
              :cancelable="true"
              submit-label="Update Location"
              @submit="submitUpdate"
              @cancel="editingOperation = null"
            />
          </div>
          <div v-else class="created-operation-row d-flex align-items-center justify-content-between px-3 py-2 mb-1 rounded">
            <div>
              <div class="d-flex align-items-center">
                <i class="fas fa-map-marker-alt text-muted mr-2"></i>
                <span class="font-weight-bold mr-2">{{ op.name }}</span>
                <span v-if="op.operation_type" class="text-muted">{{ op.operation_type.name }}</span>
              </div>
              <div class="text-sm text-muted ml-4">
                {{ [op.line1, op.city, op.state?.text, op.zip].filter(Boolean).join(", ") }}
              </div>
              <div v-if="op.state_license" class="text-sm text-muted ml-4">License: {{ op.state_license }}</div>
            </div>
            <BushelBtn variant="primary" class="btn-sm ml-3 text-nowrap" flat @click="editingOperation = op"> Edit </BushelBtn>
          </div>
        </div>
      </div>

      <h5 v-if="!editingOperation" class="mb-3 text-primary">
        {{ createdOperations.length ? "Add Another Location" : "Add Your First Location" }}
      </h5>
      <OperationForm
        v-if="!editingOperation"
        ref="createForm"
        :countries="countries"
        :countries-loading="countriesLoading"
        :operation-types="operationTypes"
        :saving="creating"
        :error="createError"
        :show-company-fields="!companySaved"
        :current-company="currentCompany"
        @submit="submitCreate"
      >
        <template #logo>
          <ConstrainImageCropperNoPreview
            class="rounded w-100"
            doc-label="Logo"
            input-id="logo"
            :file-url="currentCompany && !currentCompany.logoLink.includes('gravatar') ? currentCompany.logoLink : ''"
            no-overlay
            cropping-height="150px"
            display-height="150px"
            @file-added="(file) => (companyLogo = file)"
          />
        </template>
      </OperationForm>
    </div>
    <BushelBtn v-if="createdOperations.length && !editingOperation" class="btn-block mt-3" variant="primary" @click="finish">
      Finished Adding Locations/Licenses
    </BushelBtn>
  </BModal>
</template>

<script>
import { mapState, mapActions } from "vuex";
import ConstrainImageCropperNoPreview from "@/components/common/ConstrainImageCropperNoPreview.vue";
import OperationForm from "./OperationForm.vue";
import * as api from "@/services/api";

export default {
  components: {
    ConstrainImageCropperNoPreview,
    OperationForm,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    currentCompany: {
      type: Object,
      default: () => {},
    },
    sellerCompanyId: {
      type: Number,
      default: null,
    },
    sellerCompanyName: {
      type: String,
      default: null,
    },
  },
  emits: ["operationCreated", "hide"],
  data() {
    return {
      companyLogo: null,
      industry: APP_INDUSTRY,
      appName: APP_NAME,
      countries: [],
      countriesLoading: false,
      creating: false,
      createError: false,
      updating: false,
      updateError: false,
      createdOperations: [],
      companySaved: false,
      suggestions: [],
      suggestionError: false,
      creatingSuggestion: null,
      editingOperation: null,
    };
  },
  computed: {
    ...mapState("base", {
      operationTypes: (state) => state.operationTypes,
    }),
  },
  mounted() {
    this.fetchOperationTypes();
    this.fetchAllCountries();
    if (this.sellerCompanyId) {
      this.fetchSuggestions();
    }
  },
  methods: {
    ...mapActions({
      createOperation: "operations/createOperation",
      updateOperation: "operations/update",
      fetchOperationTypes: "base/fetchOperationTypes",
    }),
    close() {
      this.$emit("hide", true);
    },
    fetchAllCountries() {
      this.countriesLoading = true;
      api.get("countries").then((response) => {
        this.countriesLoading = false;
        this.countries = response.data;
      });
    },
    fetchSuggestions() {
      api.get(`storefront/buyer-operation-suggestions/${this.sellerCompanyId}`).then((response) => {
        this.suggestions = response.data || [];
      });
    },
    createFromSuggestion(suggestion) {
      const retailerType = this.operationTypes.find((t) => t.name === "Retailer");
      if (!retailerType) return;

      this.creatingSuggestion = suggestion;

      const payload = {
        company_id: this.currentCompany.id,
        name: suggestion.name,
        operation_type_id: retailerType.id,
        line_one: suggestion.line1,
        line_two: suggestion.line2 || null,
        city: suggestion.city,
        state_id: suggestion.state.id,
        country_id: suggestion.country.id,
        zip: suggestion.zip,
        state_license: suggestion.state_license,
        logo: !this.companySaved ? this.companyLogo : null,
      };

      this.createOperation(payload).then(
        (response) => {
          this.creatingSuggestion = null;
          this.suggestionError = false;
          if (response?.id) {
            this.createdOperations.push({
              id: response.id,
              name: suggestion.name,
              operation_type: retailerType,
              line1: suggestion.line1,
              city: suggestion.city,
              state: suggestion.state,
              country: suggestion.country,
              zip: suggestion.zip,
              state_license: suggestion.state_license,
            });
            this.companySaved = true;
            this.suggestions = this.suggestions.filter((s) => s !== suggestion);
          }
        },
        () => {
          this.creatingSuggestion = null;
          this.suggestionError = true;
        },
      );
    },
    submitCreate(formData) {
      this.creating = true;
      this.createError = false;
      const payload = {
        company_id: this.currentCompany.id,
        name: formData.name,
        operation_type_id: formData.operation_type.id,
        summary: formData.summary,
        line_one: formData.line1,
        line_two: formData.line2,
        city: formData.city,
        state_id: formData.state.id,
        country_id: formData.country.id,
        zip: formData.zip,
        state_license: formData.state_license || null,
        state_license_doc: formData.state_license_doc,
        logo: !this.companySaved ? this.companyLogo : null,
      };
      this.createOperation(payload).then(
        (response) => {
          this.creating = false;
          if (response?.id) {
            this.createdOperations.push({
              id: response.id,
              name: formData.name,
              operation_type: formData.operation_type,
              line1: formData.line1,
              city: formData.city,
              state: formData.state,
              country: formData.country,
              zip: formData.zip,
              state_license: formData.state_license,
            });
            this.companySaved = true;
            this.$refs.createForm.reset();
          }
        },
        () => {
          this.createError = true;
          this.creating = false;
        },
      );
    },
    submitUpdate(formData) {
      this.updating = true;
      this.updateError = false;
      const payload = {
        id: this.editingOperation.id,
        name: formData.name,
        operation_type_id: formData.operation_type.id,
        operation_type: formData.operation_type,
        line_one: formData.line1,
        line_two: formData.line2,
        city: formData.city,
        state_id: formData.state.id,
        country_id: formData.country.id,
        zip: formData.zip,
        state_license: formData.state_license || null,
        state_license_doc: formData.state_license_doc,
      };
      this.updateOperation(payload).then(
        (response) => {
          this.updating = false;
          if (response?.id) {
            const index = this.createdOperations.findIndex((op) => op.id === this.editingOperation.id);
            if (index !== -1) {
              this.createdOperations[index] = {
                ...this.createdOperations[index],
                name: formData.name,
                operation_type: formData.operation_type,
                line1: formData.line1,
                city: formData.city,
                state: formData.state,
                country: formData.country,
                zip: formData.zip,
                state_license: formData.state_license,
              };
              this.createdOperations = [...this.createdOperations];
            }
            this.editingOperation = null;
          }
        },
        () => {
          this.updateError = true;
          this.updating = false;
        },
      );
    },
    finish() {
      this.$emit("operationCreated");
    },
  },
};
</script>

<style scoped>
.zip {
  position: absolute;
  bottom: 0;
  right: 20px;
  z-index: 100;
}

.welcome-banner {
  background-image: linear-gradient(to right, #565487, #535d8d, #506d95, #4790a7, #41acb6);
}

.created-operation-row {
  background: #f4f6f8;
  border: 1px solid #e2e6ea;
}

.suggestion-row {
  background: #f4f6f8;
  border: 1px solid #e2e6ea;
}
</style>
